import { NavLink as RouterLink } from 'react-router-dom'
import type { DefaultTheme } from 'styled-components'
import styled, { css } from 'styled-components'

import { applyOpacity } from '@helpers/index'

import { Button, BUTTON_CSS_VARIABLES, Input, List, Typography } from '@components/ui'
import { KeyboardArrowDownIcon, SettingsIcon } from '@components/ui/svgIcons'

import { generateMaxHeightBreakPoint, generateMaxWidthBreakPoint } from '@constants/theme'

import { NavLink } from '../NavLink'

const MENU_ITEM_MARGIN_TOP = 4

//* ProfileMenu styles

export const RootContainer = styled.div`
  display: inline-block;
  position: relative;

  ${generateMaxWidthBreakPoint('small')} {
    display: block;
  }
`

export const MenuRootContainer = styled.div`
  width: 385px;
  color: ${({ theme }) => theme.colors.common.menu};
`

export const MenuRootMobileContainer = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.common.menu};
`

export const MenuHeader = styled.div`
  height: ${({ theme }) => theme.appBarHeight}px;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.colors.common.paperStroke};
`

export const MenuFooter = styled.div`
  height: ${({ theme }) => theme.appBarHeight}px;
  padding: 15px;
  display: flex;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.common.paperStroke};
`

export const ProfileDesktopNavLink = styled(NavLink)`
  display: inline-flex;
  flex-direction: column;
  position: relative;
  padding-left: 21px;
  justify-content: center;
  color: ${({ theme }) => theme.colors.common.menu};
  text-decoration: none;

  & > ${SettingsIcon} {
    position: absolute;
    left: 0;
    top: 4px;
  }

  &:hover,
  &.active {
    color: ${({ theme }) => theme.colors.blue[40]};
  }
`

export const StyledKeyboardArrowDownIcon = styled(KeyboardArrowDownIcon)<{
  $isOpen?: boolean
}>`
  transition: transform 300ms ease-out;
  transform: rotate(0deg);
  ${({ $isOpen }) => $isOpen && `transform: rotate(180deg);`}
`

function getActiveNavStyles({ $isActive, theme }: { $isActive?: boolean; theme: DefaultTheme }) {
  const vPadding = (theme.appBarHeight - 20) / 2
  const vPaddingMedium = (theme.appBarHeightMedium - 20) / 2
  return css`
    display: inline-flex;
    height: ${theme.appBarHeight}px;
    text-decoration: none;
    line-height: 20px;
    padding: ${vPadding}px 8px;
    color: ${theme.colors.common.midnightEscape};
    border-radius: 0;

    &:hover {
      background-color: ${theme.colors.gray[2]};
    }

    ${generateMaxHeightBreakPoint('medium')} {
      height: ${theme.appBarHeightMedium}px;
      padding: ${vPaddingMedium}px 0 ${vPaddingMedium}px 8px;
    }

    ${$isActive &&
    css`
      &,
      &:hover {
        background-color: ${theme.colors.blue[40]};
        ${BUTTON_CSS_VARIABLES.backgroundColor}: ${theme.colors.blue[40]};
        ${BUTTON_CSS_VARIABLES.borderColor}: ${theme.colors.blue[40]};
        ${BUTTON_CSS_VARIABLES.color}: ${theme.colors.gray[0]};
        ${BUTTON_CSS_VARIABLES.hoverBackgroundColor}: ${theme.colors.blue[40]};
        ${BUTTON_CSS_VARIABLES.hoverBorderColor}: ${theme.colors.blue[40]};
        ${BUTTON_CSS_VARIABLES.activeBackgroundColor}: ${theme.colors.blue[40]};
        ${BUTTON_CSS_VARIABLES.focusBorderColor}: ${theme.colors.blue[40]};
        color: ${theme.colors.gray[0]};
      }
    `}
  `
}

export const StyledNavLink = styled(NavLink)<{ $isActive?: boolean }>`
  ${getActiveNavStyles}
`

export const StyledNavButton = styled(Button)<{ $isActive?: boolean }>`
  ${getActiveNavStyles}
  transition: transform 400ms linear;

  &::before {
    border: none;
  }

  &[aria-expanded='true'] ${StyledKeyboardArrowDownIcon} {
    transform: rotateX(180deg);
  }
`

//* MenuItemWithNavigation styles

export const CompanySelectLink = styled(RouterLink)`
  min-width: 0;
  margin-right: 15px;
  color: inherit;
  text-decoration: none;
  display: inline-flex;
  flex-direction: column;
  line-height: 1.2;

  & > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
  }
`

export const CompanySettingsLink = styled(RouterLink)`
  padding: 0 10px;
  color: inherit;
  display: inline-flex;
  height: 100%;
  align-items: center;
  border-left: 1px solid ${({ theme }) => theme.colors.common.paperStroke};
`

//* SearchableCompanyMenu styles:

export const SearchWrapper = styled.div`
  padding: 10px 15px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.common.paperStroke};
`

export const SearchInput = styled(Input)`
  width: 100%;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray[100]};
  font-weight: 600;
  height: 32px;
  --input-border-color: ${({ theme }) => theme.colors.gray[80]};

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray[2]};
    opacity: 1;
  }
`

export const NoResultTypography = styled(Typography).attrs({
  color: 'gray-40',
  size: '400-sm',
})`
  padding: 10px 15px;
`

export const ScrollableMenuWrapper = styled.div<{ $shadowVisible: boolean }>`
  position: relative;

  ${({ theme, $shadowVisible }) =>
    $shadowVisible &&
    css`
      &:before,
      &:after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        height: 5px;
        mix-blend-mode: multiply;
        opacity: 0.1;
      }
      &:before {
        top: 0;
        background: ${`linear-gradient(180deg, ${theme.colors.blue[40]} 0%, ${applyOpacity(
          theme.colors.blue[40],
          0
        )} 100%)`};
      }
      &:after {
        bottom: 0;
        background: ${`linear-gradient(0deg, ${theme.colors.blue[40]} 0%, ${applyOpacity(
          theme.colors.blue[40],
          0
        )} 100%)`};
      }
    `}
`

export const StyledMenuList = styled(List).attrs({
  menu: true,
  disablePadding: true,
})`
  max-height: ${({ theme }) => 5 * theme.appBarHeight - MENU_ITEM_MARGIN_TOP}px;
  overflow-y: auto;

  & > li {
    font-weight: 600;
    font-size: 14px;
    height: ${({ theme }) => theme.appBarHeight - MENU_ITEM_MARGIN_TOP}px;
    box-sizing: border-box;
    color: ${({ theme }) => theme.colors.common.menu};
    justify-content: space-between;
    padding: 10px 0 10px 15px;

    &:hover {
      cursor: auto;
    }

    &:not([data-selected='true']) ${CompanySelectLink}, &:not([data-selected='true']) ${CompanySettingsLink} {
      &:hover,
      &:focus {
        color: ${({ theme }) => theme.colors.blue[40]};
      }
    }
  }
`
